// krida-ui-board.js - Board rendering logic for Krida Patra game

import { createCardElement } from './card-renderer.js';
import { createStackContainer } from './stack-container.js';
import { getCardNumericValue, getCardPointValue, SUIT_SYMBOLS } from './krida-patra-helpers.js';
import * as pileDetailsModal from './krida-ui-pile-details.js';

// --- Module State & Dependencies ---
let elements = {}; // Cache relevant elements passed from main UI
let selectedBoardPileIndices = []; // Track selection state in this module

// Create helpers object for stack container
const helpers = {
  getCardPointValue,
  SUIT_SYMBOLS,
  getCardNumericValue
};

// --- Initialization --- 
export function init(dependencies) {
  console.log("KridaUIBoard: Initializing...");
  elements.boardContent = dependencies.elements?.boardContent;
  if (!elements.boardContent) console.error("KridaUIBoard: Missing dependency: elements.boardContent");
  if (!createStackContainer) console.error("KridaUIBoard: Missing dependency: createStackContainer (import failed?)");
  console.log("KridaUIBoard: Initialized.");
}

// --- Board Rendering Functions --- 

// Update the board - called from krida-main.js (via KridaUI)
export function updateBoard(board, externalSelectedIndices, isMyTurn) {
  // Always use external selection when provided (even if empty array)
  // Only fall back to internal state if external selection is not provided at all
  const currentSelectedIndices = externalSelectedIndices !== undefined ? externalSelectedIndices : selectedBoardPileIndices;
  
  // Update internal state to match external state when provided
  if (externalSelectedIndices !== undefined) {
    selectedBoardPileIndices = [...externalSelectedIndices];
  }
  
  console.log("KridaUIBoard: Updating board with piles:", board, "Selected:", currentSelectedIndices, "External provided:", externalSelectedIndices !== undefined);
  if (!elements.boardContent) {
      console.error("KridaUIBoard: Board content element not found");
      return;
  }
  elements.boardContent.innerHTML = ''; // Clear previous board
  elements.boardContent.classList.toggle('not-my-turn', !isMyTurn);

  if (!board || board.length === 0) {
      elements.boardContent.innerHTML = '<div class="empty-board-message">Board is empty</div>';
      return;
  }
  
  const pilesWithValues = board.map((pileData, index) => ({
    pileData: pileData,
    originalIndex: index,
    value: computePileValue(pileData)
  }));

  pilesWithValues.sort((a, b) => b.value - a.value);

  pilesWithValues.forEach(pileInfo => {
    const { pileData, originalIndex: index, value: pileValue } = pileInfo;

    let visibleCardCount = (pileData.visible && Array.isArray(pileData.visible)) ? pileData.visible.length : 0;
    let hiddenCardCount = (pileData.hidden && Array.isArray(pileData.hidden)) ? pileData.hidden.length : 0;
    let pileCardCount = visibleCardCount + hiddenCardCount; 
    let pilePoints = calculateTotalPilePoints_UI(pileData);
    let cardsToDisplay = (pileData.visible && Array.isArray(pileData.visible)) ? pileData.visible : [];

    const config = {
        type: 'pile',
        helpers: helpers, // Add the helpers object
        cards: cardsToDisplay,
        scale: 1.0,
        pileDetailsModal: pileDetailsModal,
        pileIndex: index,
        pileValue: pileValue,
        pilePoints: pilePoints,
        cardCount: pileCardCount,
        isSelected: currentSelectedIndices.includes(index),
        pileData: pileData
    };

    const containerResult = createStackContainer(cardsToDisplay, config);

    if (containerResult && containerResult.element) {
        // Add click handler for pile selection
        containerResult.element.addEventListener('click', function(e) {
            console.log(`KridaUIBoard: Pile clicked at index ${index}`);
            
            // Toggle selection in this module
            if (selectedBoardPileIndices.includes(index)) {
                selectedBoardPileIndices = selectedBoardPileIndices.filter(i => i !== index);
            } else {
                selectedBoardPileIndices.push(index);
            }
            
            // Update visual selection immediately
            containerResult.element.classList.toggle('selected', selectedBoardPileIndices.includes(index));
            
            // Dispatch event to notify main of selection change
            const selectEvent = new CustomEvent('krida-board-selection-changed', {
                bubbles: true,
                cancelable: true,
                detail: {
                    selectedIndices: [...selectedBoardPileIndices],
                    pileIndex: index,
                    pileData: pileData,
                    pileValue: pileValue
                }
            });
            
            containerResult.element.dispatchEvent(selectEvent);
        });
        
        elements.boardContent.appendChild(containerResult.element);
    } else {
        console.error(`KridaUIBoard: Failed to create stack container for pile index ${index}`);
        const errorPlaceholder = document.createElement('div');
        errorPlaceholder.textContent = `[Error Pile ${index}]`;
        errorPlaceholder.style.border = '1px dashed red';
        errorPlaceholder.style.padding = '20px';
        errorPlaceholder.style.margin = '5px';
        elements.boardContent.appendChild(errorPlaceholder);
    }
  });

  console.log("KridaUIBoard: Board updated with", board.length, "piles");
}

export function computePileValue(pile) {
    if (!pile || typeof pile !== 'object' || pile === null || Array.isArray(pile)) {
        return 0;
    }
    let sum = 0;
    if (pile.visible && Array.isArray(pile.visible)) {
        pile.visible.forEach(card => {
            const value = card.numericValue !== undefined ? card.numericValue : getCardNumericValue(card);
            if (!isNaN(value)) sum += value;
        });
    }
    return sum;
}

export function calculateTotalPilePoints_UI(pile) {
    let totalPoints = 0;
    if (!pile || typeof pile !== 'object' || Array.isArray(pile)) return 0;
    
    const cards = [];
    if (pile.visible && Array.isArray(pile.visible)) cards.push(...pile.visible);
    if (pile.hidden && Array.isArray(pile.hidden)) cards.push(...pile.hidden);

    cards.forEach(card => {
         const pointValue = getCardPointValue(card);
         if (!isNaN(pointValue)) totalPoints += pointValue;
    });
    return totalPoints;
} 